import { Box } from '@mui/material';
import {
  Children,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import type { ImperativePanelHandle } from 'react-resizable-panels';

import { PanelContent, type PanelContentProps } from './PanelContent';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { zIndex } from '@/theme';

/**
 * The design system's page-splitting primitive, ported from
 * Web/src/components/layouts/Panels/ on the same library version
 * (react-resizable-panels ^3.0.6). See
 * docs/adr/0013-resizable-panels-for-page-layout.md.
 *
 * What the library gives us for free, and what this app had none of before:
 * drag-to-resize, keyboard resize (the handle is a `role="separator"` that
 * responds to arrows and Home/End), and layout persistence to localStorage keyed
 * on `autoSaveId`. What this component adds on top, matching Web: a 2:1 default
 * split, double-click on the handle to reset it, a collapsible drawer (which can
 * land collapsed — `defaultCollapsed`), Escape to collapse with focus handed back
 * to whatever opened it, and a polite live-region announcement so the collapse is
 * not silent to a screen reader.
 *
 * Deliberately *not* ported: Web's `cloneElement` + `displayName` sniffing to
 * inject props into children. Context does the same job without the `as any`
 * cast that approach needs for `ref`.
 */

type PanelsContextValue = {
  drawerCollapsed: boolean;
  collapseDrawer: (options?: { notify?: boolean }) => void;
  expandDrawer: () => void;
  /**
   * State-only setter for the library's own onCollapse/onExpand callbacks. Those
   * fire when the *user drags* the handle past the collapse threshold, at which
   * point the panel has already moved — calling collapseDrawer/expandDrawer back
   * would re-drive the panel and can oscillate.
   */
  setDrawerCollapsed: (collapsed: boolean) => void;
  drawerPanelRef: RefObject<ImperativePanelHandle | null>;
  drawerContentRef: RefObject<HTMLDivElement | null>;
  mainPanelRef: RefObject<ImperativePanelHandle | null>;
  defaultMainSize: number;
  defaultCollapsed: boolean;
};

const PanelsContext = createContext<PanelsContextValue | null>(null);

const usePanels = (component: string): PanelsContextValue => {
  const context = useContext(PanelsContext);
  if (!context) throw new Error(`${component} must be rendered inside <Panels>`);
  return context;
};

export type PanelsProps = {
  children: ReactNode;
  /**
   * Key the persisted layout is stored under
   * (`react-resizable-panels:<autoSaveId>` in localStorage). Required, because a
   * layout the user drags and then loses on navigation is worse than no drag at
   * all — that was the bug in the panel this replaces.
   */
  autoSaveId: string;
  direction?: 'horizontal' | 'vertical';
  defaultLayoutRatio?: '2:1' | '1:1';
  /**
   * Land with the drawer collapsed to its rail — for a page state where the
   * panel has nothing to show yet (Financials before its first contract).
   *
   * A *default*, not a mode: a layout persisted under `autoSaveId` always wins,
   * so a user who opens the drawer and comes back finds it open, which is the
   * whole point of `autoSaveId`. Assumes `Panels.Drawer`'s default
   * `collapsedSize` — see the comment at the drawer's `defaultSize`.
   */
  defaultCollapsed?: boolean;
  /** Controlled drawer state — pass to drive the drawer from a selection. */
  expandDrawer?: boolean;
  onDrawerCollapse?: () => void;
  collapseDrawerOnEscape?: boolean;
  /** Called after Escape collapses the drawer, so the caller can restore focus. */
  onEscapeFromDrawer?: () => void;
};

const RATIOS: Record<'2:1' | '1:1', number> = {
  '2:1': (2 / 3) * 100,
  '1:1': 50,
};

/** Minimum share of the group each panel keeps, matching the design system. */
const MAIN_MIN_SIZE = 40;
const DRAWER_MIN_SIZE = 15;
const DRAWER_COLLAPSED_SIZE = 3;

/**
 * Floor for the main panel, in pixels.
 *
 * `minSize` above is a *percentage of the group*, which cannot express "never
 * narrower than usable" — on a small window 40% is far below the width the
 * Financials contract editor needs, and the category cards used to overflow their
 * container. Flexbox honours a CSS `min-width` against the inline `flex` the
 * library sets, which is the same trick the drawer uses for its 50px floor.
 *
 * 600 comes from the Financials width budget, which is the tightest page:
 *
 *   600 − 48 (PanelContent padding) − 208 (contracts rail + gap) = 344
 *   344 − 40 (ContractPanel padding) = 304 of category content
 *
 * 304 comfortably clears the ~240px the rate-card table needs, and sits just
 * under the 460px two-column step so the grid settles on one clean column rather
 * than two cramped ones.
 */
const MAIN_MIN_WIDTH_PX = 600;

/**
 * The gutter between panels.
 *
 * 2px of grey `PanelGroup` showing between two white `PanelContent` cards is what
 * makes the split visible — the handle itself paints nothing at rest. The hover
 * and drag states are ours rather than the design system's: a visible line is not
 * the same as a discoverable one, and there was no affordance telling you it could
 * be dragged. The library stamps `data-resize-handle-state="drag"` while dragging.
 */
const handleSx = {
  width: 2,
  minHeight: '100%',
  cursor: 'ew-resize',
  position: 'relative',
  flex: 'none',
  transition: 'background-color var(--duration-instant) ease-in',
  '&:hover': { bgcolor: 'var(--color-fog-40)' },
  '&[data-resize-handle-state="drag"]': { bgcolor: 'var(--color-blue-80)' },
  '&:focus-visible': { outline: 'var(--focus-ring)', outlineOffset: '1px' },
  // Widens the grab target to 10px without moving the 2px line.
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    width: 'var(--spacing-unit-half)',
    height: '100%',
  },
  '&::before': { right: '100%' },
  '&::after': { left: '100%' },
} as const;

const MainPanel = ({
  children,
  footer,
  panelContentProps = { hideOverflow: false, hasPaddingBottom: true },
  contentRef,
  minWidth = MAIN_MIN_WIDTH_PX,
}: {
  children: ReactNode;
  /**
   * Pinned to the panel's bottom-right corner, over the scrolling content —
   * for status that must stay readable however far the content has scrolled.
   *
   * A slot rather than a sticky last child, because sticky bottom only reaches
   * the panel's edge once the content is taller than the panel: on a short
   * page it would park in the middle of the card. Positioned against the
   * `Panel` itself, so it is unaffected by scroll position and by
   * `PanelContent`'s padding.
   */
  footer?: ReactNode;
  panelContentProps?: Omit<PanelContentProps, 'children'>;
  /**
   * The panel's scroll node. Virtualised content needs it: `useVirtualizer`
   * measures against whichever element actually scrolls, which is PanelContent.
   */
  contentRef?: Ref<HTMLDivElement>;
  /** Pixel floor, on top of the percentage `minSize`. See MAIN_MIN_WIDTH_PX. */
  minWidth?: number;
}) => {
  const { mainPanelRef, defaultMainSize, defaultCollapsed } = usePanels('Panels.Main');

  return (
    <Panel
      ref={mainPanelRef}
      minSize={MAIN_MIN_SIZE}
      /*
        Nothing when the drawer starts collapsed: the drawer declares the only
        `defaultSize` and `calculateUnsafeDefaultLayout` hands the whole
        remainder to the panels that declared none — so main lands on exactly
        `100 - collapsedSize` for any collapsed size, and the group totals 100.
        Declaring both would make that total 69.67, which
        `validatePanelGroupLayout` warns about and renormalises on every mount.
      */
      defaultSize={defaultCollapsed ? undefined : defaultMainSize}
      style={{ minWidth, position: footer ? 'relative' : undefined }}
    >
      <PanelContent ref={contentRef} {...panelContentProps}>
        {children}
      </PanelContent>
      {footer ? (
        <Box
          data-automation-id="panel-footer"
          sx={{
            position: 'absolute',
            right: 'var(--spacing-unit-3)',
            bottom: 'var(--spacing-unit-2)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            // The bar floats over the last rows, so only its own chips may take
            // the pointer — otherwise it would swallow clicks on the content.
            pointerEvents: 'none',
            '& > *': { pointerEvents: 'auto' },
            zIndex: zIndex.stickyHeader,
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Panel>
  );
};

const SecondaryPanel = ({
  children,
  panelContentProps = { hideOverflow: false, hasPaddingBottom: true },
}: {
  children: ReactNode;
  panelContentProps?: Omit<PanelContentProps, 'children'>;
}) => (
  <Panel minSize={20}>
    <PanelContent {...panelContentProps}>{children}</PanelContent>
  </Panel>
);

const DrawerPanel = ({
  children,
  label,
  heading,
  collapsedContent,
  minSize = DRAWER_MIN_SIZE,
  collapsedSize = DRAWER_COLLAPSED_SIZE,
  panelContentProps = { hasPaddingBottom: true },
}: {
  children: ReactNode;
  /** Names the drawer in the collapse/expand control's accessible name. */
  label?: string;
  /**
   * Rendered on the collapse control's row, to its left, while the drawer is
   * open — so a rail's first section title shares the line with the chevron
   * instead of sitting under an otherwise empty row (2026-09-25, the pilot's
   * ask on Planning). Styled by the caller; the row only lays it out.
   */
  heading?: ReactNode;
  /**
   * Shown under the expand control while the drawer is collapsed — the rail
   * itself, for signals that should not need the drawer opened to be seen
   * (Project details' missing count and new changes; docs/briefs/
   * project-details-v2, decision 7). Nothing is rendered when it is absent,
   * so every other drawer's rail is exactly as it was.
   */
  collapsedContent?: ReactNode;
  minSize?: number;
  collapsedSize?: number;
  panelContentProps?: Omit<PanelContentProps, 'children'>;
}) => {
  const {
    drawerCollapsed,
    collapseDrawer,
    expandDrawer,
    setDrawerCollapsed,
    drawerPanelRef,
    drawerContentRef,
    defaultCollapsed,
  } = usePanels('Panels.Drawer');
  const { t } = useTranslation();
  const drawerLabel = label ?? t('panels.DefaultDrawerLabel');
  const toggleLabel = drawerCollapsed
    ? t('panels.ExpandDrawer', { label: drawerLabel })
    : t('panels.CollapseDrawer', { label: drawerLabel });

  return (
    <Panel
      ref={drawerPanelRef}
      collapsible
      collapsedSize={collapsedSize}
      minSize={minSize}
      /*
        This — not `Panels`' seeded `useState` — is what makes `defaultCollapsed`
        real. On mount the library computes a layout and calls
        `callPanelCallbacks` unconditionally, which fires `onExpand` for any
        drawer whose computed size differs from `collapsedSize`; a drawer with no
        `defaultSize` computes to the leftover 33.33, so the seeded state was
        overwritten every time. Declaring the collapsed size makes the library's
        own default layout the collapsed one and fires `onCollapse` instead.
        It all happens in a layout effect, so nothing paints expanded first —
        which a post-mount `useEffect` calling `collapse()` cannot promise.
      */
      defaultSize={defaultCollapsed ? collapsedSize : undefined}
      // A percentage collapse would vanish on a narrow window; the floor keeps
      // the expand affordance reachable.
      style={{ minWidth: 50 }}
      onCollapse={() => setDrawerCollapsed(true)}
      onExpand={() => setDrawerCollapsed(false)}
    >
      <PanelContent
        ref={drawerContentRef}
        {...panelContentProps}
        hideOverflow={drawerCollapsed || panelContentProps.hideOverflow}
        data-automation-id="panels-drawer"
      >
        <Box
          data-automation-id="panels-drawer-bar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            justifyContent: drawerCollapsed ? 'center' : 'flex-end',
            position: 'sticky',
            /*
              Sticks at the scroller's CONTENT edge, not its padding edge — so
              `top: 0` left PanelContent's 24px of top padding above the row,
              and scrolled content showed through it. The row therefore starts
              one padding above its natural place (negative margin, padding
              back) and sticks that far above the content edge, which is the
              visible top of the scroller.
            */
            top: drawerCollapsed ? 0 : 'calc(-1 * var(--spacing-unit-3))',
            zIndex: 'var(--z-sticky-header)',
            /*
              Opaque, and full-bleed across PanelContent's padding: the row is
              sticky, so once the drawer scrolls the content passes under it.
              With only a chevron here nobody noticed; with a heading and a
              button on the row (2026-09-25) the text painted over whatever
              scrolled beneath. Collapsed, there is no padding to swallow.
            */
            bgcolor: 'var(--bg-panel)',
            mx: drawerCollapsed ? 0 : 'calc(-1 * var(--spacing-unit-3))',
            px: drawerCollapsed ? 0 : 'var(--spacing-unit-3)',
            mt: drawerCollapsed ? 0 : 'calc(-1 * var(--spacing-unit-3))',
            pt: drawerCollapsed ? 0 : 'var(--spacing-unit-3)',
            pb: 0.5,
          }}
        >
          {drawerCollapsed || heading === undefined ? null : (
            <Box data-automation-id="panels-drawer-heading" sx={{ flex: 1, minWidth: 0 }}>
              {heading}
            </Box>
          )}
          <Box
            component="button"
            type="button"
            onClick={drawerCollapsed ? expandDrawer : () => collapseDrawer()}
            // The icon is aria-hidden, so without this the control has no
            // accessible name at all — the defect this replaces.
            aria-label={toggleLabel}
            aria-expanded={!drawerCollapsed}
            title={toggleLabel}
            data-automation-id="panels-drawer-toggle"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'var(--input-height-tiny)',
              height: 'var(--input-height-tiny)',
              border: 'none',
              borderRadius: 'var(--border-radius-small)',
              bgcolor: 'transparent',
              color: 'var(--fg-3)',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'var(--bg-hover)', color: 'var(--fg-1)' },
              '&:focus-visible': { outline: 'var(--focus-ring)', outlineOffset: '1px' },
            }}
          >
            <MaterialSymbol
              name={drawerCollapsed ? 'keyboard_arrow_left' : 'keyboard_arrow_right'}
              size={16}
            />
          </Box>
        </Box>
        {/* Unmounted while collapsed so nothing inside stays tabbable. */}
        {drawerCollapsed ? (collapsedContent ?? null) : children}
      </PanelContent>
    </Panel>
  );
};

/**
 * Open or close the drawer from inside it — a collapsed rail's own buttons
 * (`collapsedContent`) open the drawer they sit in.
 */
export const useDrawerControls = () => {
  const { drawerCollapsed, expandDrawer, collapseDrawer } = usePanels('useDrawerControls');
  return { drawerCollapsed, expandDrawer, collapseDrawer };
};

export const Panels = ({
  children,
  autoSaveId,
  direction = 'horizontal',
  defaultLayoutRatio = '2:1',
  defaultCollapsed = false,
  expandDrawer: expandDrawerProp,
  onDrawerCollapse,
  collapseDrawerOnEscape = false,
  onEscapeFromDrawer,
}: PanelsProps) => {
  const { t } = useTranslation();
  const [drawerCollapsed, setDrawerCollapsed] = useState(defaultCollapsed);
  const drawerPanelRef = useRef<ImperativePanelHandle>(null);
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const mainPanelRef = useRef<ImperativePanelHandle>(null);

  const defaultMainSize = RATIOS[defaultLayoutRatio];

  /**
   * These only drive the panel. React state is updated by the library's own
   * onCollapse/onExpand, which fire for our imperative calls and for a user drag
   * alike — so `drawerCollapsed` has exactly one writer and cannot disagree with
   * the panel's real position.
   */
  const collapseDrawer = useCallback(
    (options?: { notify?: boolean }) => {
      drawerPanelRef.current?.collapse();
      if (options?.notify !== false) onDrawerCollapse?.();
    },
    [onDrawerCollapse],
  );

  const expandDrawer = useCallback(() => {
    /*
      The argument is the library's `minSizeOverride`, which it uses as the base
      size when the drawer has no remembered pre-collapse width — as a drawer
      that *started* collapsed (`defaultCollapsed`) never does. That is what
      makes the first expand land on the 2:1 split instead of on `minSize` (15).
    */
    drawerPanelRef.current?.expand(100 - defaultMainSize);
  }, [defaultMainSize]);

  // Keep the drawer in step when a caller drives it from a selection.
  useEffect(() => {
    if (expandDrawerProp === undefined) return;
    if (expandDrawerProp) expandDrawer();
    else collapseDrawer({ notify: false });
  }, [expandDrawerProp, expandDrawer, collapseDrawer]);

  useEffect(() => {
    if (!collapseDrawerOnEscape) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || drawerCollapsed) return;

      /**
       * Only when focus is actually inside the drawer. Web collapses on Escape
       * from anywhere on the page, but this app cannot: the plan editor already
       * owns Escape to close the open row card and to cancel a drag, so a global
       * handler here swallows those. Scoping it to the drawer keeps both.
       */
      const drawerHasFocus = drawerContentRef.current?.contains(document.activeElement) ?? false;
      if (!drawerHasFocus) return;

      // Focus is inside the drawer we are about to unmount, so it has to go
      // somewhere deliberate. Two frames: one for React's re-render, one for the
      // panel to finish collapsing, then the caller decides where focus lands.
      event.preventDefault();
      collapseDrawer();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => onEscapeFromDrawer?.());
      });
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [collapseDrawerOnEscape, drawerCollapsed, collapseDrawer, onEscapeFromDrawer]);

  const resetLayout = useCallback(() => {
    mainPanelRef.current?.resize(defaultMainSize);
  }, [defaultMainSize]);

  const context = useMemo<PanelsContextValue>(
    () => ({
      drawerCollapsed,
      collapseDrawer,
      expandDrawer,
      setDrawerCollapsed,
      drawerPanelRef,
      drawerContentRef,
      mainPanelRef,
      defaultMainSize,
      defaultCollapsed,
    }),
    [drawerCollapsed, collapseDrawer, expandDrawer, defaultMainSize, defaultCollapsed],
  );

  // A handle between every pair of panels, never after the last one.
  const panels = Children.toArray(children);
  const withHandles = panels.flatMap((panel, index) =>
    index < panels.length - 1
      ? [
          panel,
          <Box
            key={`handle-${String(index)}`}
            component={PanelResizeHandle}
            onDoubleClick={resetLayout}
            data-automation-id="panels-resize-handle"
            sx={handleSx}
          />,
        ]
      : [panel],
  );

  return (
    <PanelsContext.Provider value={context}>
      <PanelGroup
        direction={direction}
        autoSaveId={autoSaveId}
        style={{ backgroundColor: 'var(--bg-app)' }}
      >
        {/*
          Announces the drawer state. `aria-live="polite"` rather than assertive:
          collapsing a panel is a confirmation, not an interruption.
        */}
        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
            border: 0,
            p: 0,
            m: -1,
          }}
        >
          {drawerCollapsed ? t('panels.DrawerClosed') : t('panels.DrawerOpened')}
        </Box>
        {withHandles}
      </PanelGroup>
    </PanelsContext.Provider>
  );
};

Panels.Main = MainPanel;
Panels.Secondary = SecondaryPanel;
Panels.Drawer = DrawerPanel;
